import { axiosInstance } from "../rest/axios";
import { firebaseDb } from "../firebase/client-app";
import {
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  query,
  where,
  Firestore,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
} from "firebase/firestore/lite";
import { DbUser, DeviceDetail } from "./models";
import { createChunks } from "../util/array-extensions";
import { DEFAULT_DIAL_CODE } from "../util/country-codes";
import { getCookie } from "cookies-next";

export class UserService {
  private ref(firestore: Firestore) {
    return collection(firestore, "users");
  }

  private userSessionRef(firestore: Firestore, userId: string) {
    return collection(firestore, "users/" + userId + "/user_sessions");
  }

  async getUsersByIds(
    userIds: string[],
    firestore: Firestore = firebaseDb,
  ): Promise<DbUser[]> {
    if (userIds.length === 0) return [];

    const chunks = userIds.length > 30 ? createChunks(userIds, 30) : [userIds];

    const userPromises = chunks.map(async (chunk) => {
      const usersSnapshot = await getDocs(
        query(collection(firestore, "users"), where("id", "in", chunk)),
      );
      return usersSnapshot.docs.map((doc) => doc.data() as DbUser);
    });

    const usersArrays = await Promise.all(userPromises);
    return usersArrays.flat();
  }

  async getUser(
    userId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbUser | null> {
    const user = await getDoc(doc(this.ref(firestore), userId));
    if (!user.exists()) {
      return null;
    }
    return user.data() as DbUser;
  }

  async upsertUser(
    userId: string,
    phone: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbUser> {
    const userQuery = await getDocs(
      query(this.ref(firestore), where("phone", "==", phone)),
    );

    const existingUserDoc = userQuery.docs.find((docSnapshot) => {
      const data = docSnapshot.data() as DbUser & {
        pending_creation?: boolean;
      };
      return data.pending_creation !== true;
    });

    if (existingUserDoc) {
      return existingUserDoc.data() as DbUser;
    }
    return await this.createUser(userId, phone, firestore);
  }

  async createUser(
    userId: string,
    phone: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbUser> {
    await setDoc(doc(this.ref(firestore), userId), {
      phone: phone,
      user_name: "",
      created_at: new Date().toISOString(),
      profile_image_url: null,
      id: userId,
      auth_method: "phone",
      version: 1,
    });

    return getDoc(doc(this.ref(firestore), userId)).then(
      (doc) => doc.data() as DbUser,
    );
  }

  async upsertUserByEmail(
    userId: string,
    email: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbUser> {
    const userQuery = await getDocs(
      query(this.ref(firestore), where("email", "==", email)),
    );

    const existingUserDoc = userQuery.docs.find((docSnapshot) => {
      const data = docSnapshot.data() as DbUser & {
        pending_creation?: boolean;
      };
      return data.pending_creation !== true;
    });

    if (existingUserDoc) {
      return existingUserDoc.data() as DbUser;
    }
    return await this.createUserWithEmail(userId, email, firestore);
  }

  async createUserWithEmail(
    userId: string,
    email: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbUser> {
    await setDoc(doc(this.ref(firestore), userId), {
      email: email,
      user_name: "",
      created_at: new Date().toISOString(),
      profile_image_url: null,
      id: userId,
      auth_method: "email",
      version: 2,
    });

    return getDoc(doc(this.ref(firestore), userId)).then(
      (doc) => doc.data() as DbUser,
    );
  }

  async updateUserProfile(
    user: DbUser,
    firestore: Firestore = firebaseDb,
  ): Promise<DbUser> {
    const gender = user.gender !== undefined ? user.gender : null;
    const updatePayload: any = {
      user_name: user.user_name,
      email: user.email,
      profile_image_url: user.profile_image_url,
      gender,
    };

    await updateDoc(doc(this.ref(firestore), user.id), updatePayload);

    return getDoc(doc(this.ref(firestore), user.id)).then(
      (doc) => doc.data() as DbUser,
    );
  }

  async updateFavoriteBusiness(
    userId: string,
    businessId: string,
    add: boolean,
    firestore: Firestore = firebaseDb,
  ): Promise<void> {
    if (add) {
      await updateDoc(doc(this.ref(firestore), userId), {
        fav_business_ids: arrayUnion(businessId),
      });
    } else {
      await updateDoc(doc(this.ref(firestore), userId), {
        fav_business_ids: arrayRemove(businessId),
      });
    }
  }

  async searchUser(
    searchInput: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbUser[]> {
    if (!searchInput?.trim()) return [];

    const trimmedSearchInput = searchInput.trim().replace(/\s+/g, "");
    const formattedPhoneInput = trimmedSearchInput.startsWith("+")
      ? trimmedSearchInput
      : `${DEFAULT_DIAL_CODE}${trimmedSearchInput}`;
    const [emailDocs, phoneDocs] = await Promise.all([
      getDocs(
        query(this.ref(firestore), where("email", "==", searchInput.trim())),
      ),
      getDocs(
        query(this.ref(firestore), where("phone", "==", formattedPhoneInput)),
      ),
    ]);

    const combinedUsers = [...emailDocs.docs, ...phoneDocs.docs];

    // Remove duplicates by ID
    const userMap = new Map<string, DbUser>();
    combinedUsers.forEach((doc) => {
      const user = doc.data() as DbUser;
      userMap.set(user.id, user);
    });

    return Array.from(userMap.values());
  }

  async createUserSession(
    userId: string,
    data: DeviceDetail,
    token: string,
    firestore: Firestore = firebaseDb,
  ): Promise<string> {
    const sessionId = doc(this.userSessionRef(firestore, userId)).id;

    const sessionData = {
      ...data,
      id: sessionId,
      user_id: userId,
      device_fcm_token: token,
      created_at: new Date().toISOString(),
    };

    await setDoc(
      doc(this.userSessionRef(firestore, userId), sessionId),
      sessionData,
    );

    return sessionId;
  }

  async updateUserPhone(userId: string, phone: string): Promise<void> {
    if (!userId || !phone) return;
    try {
      await axiosInstance.post(
        "/user/phone",
        { user_id: userId, phone: phone },
        { headers: { Authorization: getCookie("Authorization")?.toString() } },
      );
    } catch (error) {
      throw error;
    }
  }
}

export const userService = new UserService();
